import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, Sparkles, Trophy } from "lucide-react";
import { api } from "../api";
import { useLang } from "../i18n";
import { Countdown } from "../components/Countdown";
import { Lineup } from "../components/Lineup";
import { VipSection } from "../components/VipSection";
import { SocialSection } from "../components/SocialSection";
import { Marquee } from "../components/Marquee";
import { Reveal, SectionHead } from "../components/Section";
import { NewsletterForm } from "../components/NewsletterForm";
import { ShareCountdown } from "../components/ShareCountdown";
import { GALLERY_PHOTOS, IMAGES } from "../data/site";

const HERO_LINES = ["hero_title_1", "hero_title_2", "hero_title_3"];

export default function Home() {
    const { t } = useLang();
    const [topCities, setTopCities] = useState([]);
    const { scrollY } = useScroll();
    const heroY = useTransform(scrollY, [0, 600], [0, 140]);
    const heroOpacity = useTransform(scrollY, [0, 500], [1, 0.35]);

    useEffect(() => {
        api.get("/votes").then((r) => setTopCities((r.data.cities || []).slice(0, 3))).catch(() => {});
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative min-h-screen flex items-center overflow-hidden">
                <motion.div style={{ y: heroY, opacity: heroOpacity }} className="absolute inset-0">
                    <img src={IMAGES.hero} alt="LAKOU crowd at night" className="w-full h-full object-cover animate-kenburns" />
                    <div className="absolute inset-0 bg-gradient-to-b from-lakou-void/70 via-lakou-ink/55 to-lakou-ink" />
                    <div className="absolute inset-0 bg-gradient-to-r from-lakou-void/80 via-transparent to-lakou-void/40" />
                </motion.div>
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 w-full">
                    <motion.p
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
                        data-testid="hero-overline"
                        className="font-mono2 text-xs sm:text-sm uppercase tracking-[0.3em] text-lakou-gold font-semibold flex items-center gap-2"
                    >
                        <MapPin size={14} /> {t("hero_overline")}
                    </motion.p>
                    <h1 className="mt-6 font-display font-black tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-7xl" data-testid="hero-title">
                        {HERO_LINES.map((k, i) => (
                            <motion.span
                                key={k}
                                initial={{ opacity: 0, y: 60, rotate: -1.5 }}
                                animate={{ opacity: 1, y: 0, rotate: 0 }}
                                transition={{ duration: 0.9, delay: 0.15 + i * 0.18, ease: [0.16, 1, 0.3, 1] }}
                                className={`block ${i === 2 ? "text-goldgrad" : "text-lakou-cream"}`}
                            >
                                {t(k)}
                            </motion.span>
                        ))}
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.75 }}
                        className="mt-6 max-w-xl text-base sm:text-lg text-lakou-offwhite/85 leading-relaxed"
                    >
                        {t("hero_sub")}
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.9 }}
                        className="mt-8 flex flex-wrap gap-4"
                    >
                        <Link to="/tickets" data-testid="hero-get-tickets"
                            className="inline-flex items-center gap-2 rounded-full bg-lakou-orange hover:bg-lakou-gold text-white hover:text-lakou-ink font-display font-bold text-sm uppercase tracking-[0.15em] px-8 py-4 transition-colors duration-300">
                            {t("hero_cta1")} <ArrowRight size={16} />
                        </Link>
                        <Link to="/about" data-testid="hero-discover"
                            className="inline-flex items-center gap-2 rounded-full border border-lakou-gold/40 hover:border-lakou-gold text-lakou-cream hover:text-lakou-gold font-display font-bold text-sm uppercase tracking-[0.15em] px-8 py-4 transition-colors duration-300">
                            {t("hero_cta2")}
                        </Link>
                    </motion.div>
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.05 }} className="mt-12">
                        <Countdown />
                    </motion.div>
                    <motion.p
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.3 }}
                        className="mt-8 font-mono2 text-[10px] sm:text-xs uppercase tracking-[0.3em] text-lakou-gold"
                        data-testid="hero-urgency"
                    >
                        {t("urgency_line")}
                    </motion.p>
                </div>
            </section>

            <Marquee />

            {/* INTRO — WHERE MUSIC BECOMES AN EXPERIENCE */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32" data-testid="intro-section">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-7">
                        <Reveal>
                            <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-lakou-gold font-semibold">{t("intro_eyebrow")}</p>
                            <h2 className="mt-5 font-display font-black tracking-tight leading-[1.05] text-3xl sm:text-5xl text-lakou-cream" data-testid="intro-title">
                                {t("intro_title")}
                            </h2>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-5">
                        <Reveal delay={0.15}>
                            <p className="text-sm sm:text-base text-lakou-grey leading-relaxed" data-testid="intro-body">{t("intro_body")}</p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* ABOUT TEASER */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                    <Reveal>
                        <div className="relative">
                            <div className="rounded-3xl overflow-hidden border border-lakou-gold/20 aspect-[4/5]">
                                <img src={IMAGES.flag} alt="Haitian flag at a LAKOU night" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                            </div>
                            <div className="absolute -bottom-8 -right-4 sm:-right-8 w-40 sm:w-52 rounded-2xl overflow-hidden border border-lakou-gold/30 shadow-2xl rotate-3 aspect-square">
                                <img src={IMAGES.lakouShirt} alt="LAKOU EST. 2025 shirt" className="w-full h-full object-cover" />
                            </div>
                        </div>
                    </Reveal>
                    <div>
                        <SectionHead eyebrow={t("home_movement")} title={t("footer_line")} testId="home-about-title" />
                        <Reveal delay={0.15}>
                            <p className="mt-6 text-lakou-grey leading-relaxed">
                                LAKOU is a Haitian-born cultural and musical experience — where Afro House, Amapiano, and the drum
                                of the motherland meet Haitian soul. LAKOU exists to create world-class moments, at home, for our people first.
                            </p>
                            <Link to="/about" data-testid="home-about-link"
                                className="mt-8 inline-flex items-center gap-2 font-mono2 text-xs uppercase tracking-[0.2em] text-lakou-gold hover:text-lakou-goldbright transition-colors">
                                Our story <ArrowRight size={14} />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* LINEUP */}
            <Lineup />

            {/* VIP RESERVATIONS */}
            <VipSection />

            {/* SOCIAL */}
            <SocialSection />

            {/* VOTE TEASER */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <SectionHead eyebrow="One island, many cities" title={t("vote_title")} testId="home-vote-title" />
                        <Reveal delay={0.1}>
                            <p className="mt-6 text-lakou-grey leading-relaxed">{t("vote_sub")}</p>
                            <Link to="/vote" data-testid="home-vote-cta"
                                className="mt-8 inline-flex items-center gap-2 rounded-full bg-lakou-orange hover:bg-lakou-gold text-white hover:text-lakou-ink font-display font-bold text-xs uppercase tracking-[0.15em] px-8 py-4 transition-colors duration-300">
                                <MapPin size={15} /> {t("vote_cta")}
                            </Link>
                        </Reveal>
                    </div>
                    <Reveal delay={0.15}>
                        <div className="rounded-3xl border border-lakou-gold/20 bg-lakou-coal/70 p-6 sm:p-8 space-y-5" data-testid="home-vote-leaderboard">
                            {topCities.length === 0 && <p className="text-sm text-lakou-sand">Be the first to put your city on the map.</p>}
                            {topCities.map((c) => (
                                <div key={c.id} className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <span className="flex items-center gap-3 text-sm text-lakou-cream font-semibold">
                                            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-mono2 ${c.rank === 1 ? "bg-lakou-gold text-lakou-ink" : "border border-lakou-gold/30 text-lakou-gold"}`}>
                                                {c.rank === 1 ? <Trophy size={12} /> : c.rank}
                                            </span>
                                            {c.name}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* GALLERY STRIP */}
            <section className="py-24 sm:py-32 overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-wrap items-end justify-between gap-6">
                        <SectionHead eyebrow={t("home_gallery_eyebrow")} title="Lakou Nights" testId="home-gallery-title" />
                        <Link to="/gallery" data-testid="home-gallery-link"
                            className="inline-flex items-center gap-2 font-mono2 text-xs uppercase tracking-[0.2em] text-lakou-gold hover:text-lakou-goldbright transition-colors">
                            Full gallery <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
                <div className="mt-12 flex gap-4 px-4 overflow-x-auto pb-4 snap-x" data-testid="home-gallery-strip">
                    {GALLERY_PHOTOS.map((g, i) => (
                        <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                            className="snap-center shrink-0 w-64 sm:w-80 aspect-[4/5] rounded-2xl overflow-hidden border border-lakou-gold/15">
                            <img src={g.src} alt={g.caption} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* SHARE THE COUNTDOWN */}
            <section className="border-t border-lakou-gold/10 bg-lakou-coal/40 py-24 sm:py-32">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <ShareCountdown />
                </div>
            </section>

            {/* NEWSLETTER */}
            <section className="relative py-24 sm:py-32 border-t border-lakou-gold/10 overflow-hidden">
                <div className="absolute inset-0 pattern-zigzag opacity-40" aria-hidden="true" />
                <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
                    <Reveal>
                        <Sparkles size={22} className="mx-auto text-lakou-gold" />
                        <h2 className="mt-5 font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-lakou-cream" data-testid="newsletter-title">{t("newsletter_title")}</h2>
                        <p className="mt-4 text-lakou-grey">{t("newsletter_sub")}</p>
                        <div className="mt-8"><NewsletterForm /></div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
